// Admin Subscriptions tab (iter 120) — every school's plan + validity + days left.
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, RefreshCcw, IndianRupee, Search, Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ALL = "__all";

function StatusPill({ status }) {
  const map = {
    active: "bg-emerald-100 text-emerald-800",
    expired: "bg-rose-100 text-rose-700",
    none: "bg-sand-100 text-ink-700",
  };
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[0.62rem] font-bold uppercase tracking-[0.12em] ${map[status] || map.none}`}>
      {status === "active" ? "Active" : status === "expired" ? "Expired" : "No Plan"}
    </span>
  );
}

export default function AdminSubscriptions() {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ active: 0, expired: 0, none: 0 });
  const [planLabels, setPlanLabels] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [planFilter, setPlanFilter] = useState("");
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/subscriptions`);
      setRows(r.data?.rows || []);
      setCounts(r.data?.counts || { active: 0, expired: 0, none: 0 });
      setPlanLabels(r.data?.planLabels || {});
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load subscriptions");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFilter !== "all" && r.status !== statusFilter) return false;
      if (planFilter && r.planKey !== planFilter) return false;
      if (needle) {
        const hay = `${r.schoolName} ${r.udiseCode} ${r.mobile}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [rows, q, statusFilter, planFilter]);

  const downloadCSV = () => {
    const header = ["UDISE", "School", "Mobile", "Plan", "Validity Days", "Active Until", "Days Left", "Status", "Last Paid", "Renewals"];
    const lines = filtered.map((r) => [
      r.udiseCode, r.schoolName, r.mobile, r.planLabel || "", r.validityDays, r.activeUntil, r.daysRemaining, r.status, r.lastPaidAt, r.totalRenewals,
    ].map((v) => `"${String(v || "").replace(/"/g, '""')}"`).join(","));
    const csv = [header.join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `mdm-subscriptions-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4" data-testid="admin-subscriptions-tab">
      {/* Stat strip */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3">
          <p className="text-[0.65rem] font-bold uppercase tracking-wide text-emerald-800">Active subscriptions</p>
          <p className="mt-1 text-2xl font-bold text-emerald-700" data-testid="subs-count-active">{counts.active}</p>
        </div>
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-3">
          <p className="text-[0.65rem] font-bold uppercase tracking-wide text-rose-800">Expired</p>
          <p className="mt-1 text-2xl font-bold text-rose-700" data-testid="subs-count-expired">{counts.expired}</p>
        </div>
        <div className="rounded-xl border border-border bg-sand-50 p-3">
          <p className="text-[0.65rem] font-bold uppercase tracking-wide text-ink-700">Without a plan</p>
          <p className="mt-1 text-2xl font-bold text-ink-900" data-testid="subs-count-none">{counts.none}</p>
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[14rem] max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-700" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search UDISE / school / mobile" className="pl-9" data-testid="subs-search" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[10rem]" data-testid="subs-filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="expired">Expired</SelectItem>
            <SelectItem value="none">No plan</SelectItem>
          </SelectContent>
        </Select>
        <Select value={planFilter || ALL} onValueChange={(v) => setPlanFilter(v === ALL ? "" : v)}>
          <SelectTrigger className="w-[12rem]" data-testid="subs-filter-plan"><SelectValue placeholder="All plans" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All plans</SelectItem>
            {Object.entries(planLabels).map(([k, lbl]) => (
              <SelectItem key={k} value={k}>{lbl}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={load} data-testid="subs-refresh">
          <RefreshCcw className="mr-2 h-4 w-4" /> Refresh
        </Button>
        <Button variant="outline" size="sm" onClick={downloadCSV} data-testid="subs-export-csv">
          <Download className="mr-2 h-4 w-4" /> Export CSV
        </Button>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-ink-700"><Loader2 className="h-4 w-4 animate-spin" /> Loading subscriptions…</div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-border bg-white">
          <table className="min-w-full text-sm">
            <thead className="bg-sand-50 text-xs uppercase tracking-wide text-ink-700">
              <tr>
                <th className="px-4 py-3 text-left">UDISE / School</th>
                <th className="px-4 py-3 text-left">Mobile</th>
                <th className="px-4 py-3 text-left">Plan</th>
                <th className="px-4 py-3 text-left">Price</th>
                <th className="px-4 py-3 text-left">Validity</th>
                <th className="px-4 py-3 text-left">Days left</th>
                <th className="px-4 py-3 text-left">Active until</th>
                <th className="px-4 py-3 text-left">Renewals</th>
                <th className="px-4 py-3 text-left">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-ink-700">No subscriptions match the current filters.</td></tr>
              )}
              {filtered.map((r) => (
                <tr key={r.userId} className="border-t border-border" data-testid={`subs-row-${r.udiseCode}`}>
                  <td className="px-4 py-2.5">
                    <p className="font-mono text-xs text-ink-700">{r.udiseCode}</p>
                    <p className="font-medium text-ink-900">{r.schoolName || "—"}</p>
                  </td>
                  <td className="px-4 py-2.5 font-mono text-xs">{r.mobile || "—"}</td>
                  <td className="px-4 py-2.5">{r.planLabel || <span className="text-ink-700">—</span>}</td>
                  <td className="px-4 py-2.5">
                    {r.planAmount > 0 ? <span className="inline-flex items-center"><IndianRupee className="h-3 w-3" />{r.planAmount}</span> : "—"}
                  </td>
                  <td className="px-4 py-2.5">{r.validityDays ? `${r.validityDays} d` : "—"}</td>
                  <td className="px-4 py-2.5">
                    {r.status === "active" ? (
                      <span className={`font-bold ${r.daysRemaining <= 7 ? "text-rose-700" : "text-emerald-700"}`}>{r.daysRemaining} d</span>
                    ) : (
                      <span className="text-ink-700">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 font-mono text-xs">{(r.activeUntil || "").slice(0, 10) || "—"}</td>
                  <td className="px-4 py-2.5 text-center">{r.totalRenewals || 0}</td>
                  <td className="px-4 py-2.5"><StatusPill status={r.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
